import { Team } from "msd-capacity-planning-model";

export interface TeamDeltaExportSnapshot {
  getDelta(now: Date): number;
  getDeltaPercent(now: Date): number;
  getTimeoffHours(): number;
  countEngineers(): number;
  countCustomers(): number;
  countCustomersOffboarded(): number;
}

export interface TeamDeltaExportOptions {
  dates: Date[];
  exportTeams: Team[];
  nowDate: Date;
  regions: { [regionId: string]: { name?: string } };
  showCustomers: boolean;
  showEngineers: boolean;
  showOffboardings: boolean;
  showTimeOff: boolean;
  snapshots: { [date: string]: { [teamId: string]: TeamDeltaExportSnapshot } };
}

function escapeCsvValue(value: string | number): string {
  const stringValue = String(value ?? "");
  if (
    stringValue.includes(",") ||
    stringValue.includes("\"") ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, "\"\"")}"`;
  }
  return stringValue;
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function buildCsvHeaders(options: TeamDeltaExportOptions): string[] {
  const {
    dates,
    showCustomers,
    showEngineers,
    showOffboardings,
    showTimeOff,
  } = options;
  const headers = ["Team", "Region", "Types"];

  dates.forEach((date) => {
    const monthLabel = date.toISOString().substring(0, 7);
    headers.push(`${monthLabel} Delta`);
    headers.push(`${monthLabel} Delta %`);
    if (showEngineers) headers.push(`${monthLabel} Engineers`);
    if (showCustomers) headers.push(`${monthLabel} Customers`);
    if (showOffboardings) headers.push(`${monthLabel} Offboardings`);
    if (showTimeOff) headers.push(`${monthLabel} PTO Hours`);
  });

  return headers;
}

function buildCsvRow(
  team: Team,
  options: TeamDeltaExportOptions
): Array<string | number> {
  const {
    dates,
    nowDate,
    regions,
    showCustomers,
    showEngineers,
    showOffboardings,
    showTimeOff,
    snapshots,
  } = options;

  const row: Array<string | number> = [
    team.name,
    team.region ? regions[team.region]?.name || "" : "",
    (team.type || []).join(", "),
  ];

  dates.forEach((date) => {
    const snapshot = snapshots[date.toISOString().substring(0, 7)]?.[team.id];
    const delta = Math.round(snapshot?.getDelta(nowDate) || 0);
    const deltaPercent = snapshot?.getDeltaPercent(nowDate) || 0;

    row.push(delta);
    row.push(isNaN(deltaPercent) ? "" : Number(deltaPercent.toFixed(1)));
    if (showEngineers) row.push(snapshot?.countEngineers() || 0);
    if (showCustomers) row.push(snapshot?.countCustomers() || 0);
    if (showOffboardings) row.push(snapshot?.countCustomersOffboarded() || 0);
    if (showTimeOff) row.push(Math.round(snapshot?.getTimeoffHours() || 0));
  });

  return row;
}

export function downloadTeamDeltaCsv(
  filename: string,
  options: TeamDeltaExportOptions
): void {
  const headers = buildCsvHeaders(options);
  const rows = options.exportTeams.map((team) => buildCsvRow(team, options));
  const csv = [headers, ...rows]
    .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
    .join("\n");

  downloadCsv(filename, csv);
}
